import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Offcanvas, Form, Spinner, Modal } from 'react-bootstrap';
import { BsCart3, BsYoutube, BsPlus, BsDash, BsTrash, BsCheckCircleFill, BsUpload, BsX, BsSearch, BsArrowLeft } from 'react-icons/bs';
import { api, resolveMediaUrl } from '../../apis/api';
import './products.css';
import qrimag from '../../asstes/qr-img.jpeg';
import fallbackImg from '../../asstes/logo.png';

const OWNER_EMAIL = 'smart3acrackers@gmail.com';
const PACKING_CHARGE = 100;

const BANK_DETAILS = {
  name: 'SENTHILKUMAR A',
  branch: 'IOB BRANCH SEVALPATTI',
  accountNumber: '276602000000205',
  ifsc: 'IOBA0002766',
};

const INIT_FORM = { name: '', mobile: '', email: '', address: '', city: '', state: '', pincode: '', agree: false };
const PENDING_PAYMENT_KEY = 'sc_pending_payment';

function loadPendingPayment() {
  try {
    const raw = sessionStorage.getItem(PENDING_PAYMENT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data?.orderId ? data : null;
  } catch (_) {
    return null;
  }
}

function savePendingPayment(order) {
  if (!order?.orderId) return;
  sessionStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify(order));
}

function clearPendingPayment() {
  sessionStorage.removeItem(PENDING_PAYMENT_KEY);
}

function youtubeId(link) {
  if (!link) return null;
  const m = String(link).match(/(?:v=|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

function productImgSrc(src) {
  return resolveMediaUrl(src) || fallbackImg;
}

function handleImgError(e) {
  if (e.currentTarget.src !== fallbackImg) {
    e.currentTarget.src = fallbackImg;
  }
}

export const Products = () => {
  const [categories, setCategories] = useState(['All']);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState({});
  const [showCart, setShowCart] = useState(false);
  const [step, setStep] = useState('cart');
  const [form, setForm] = useState(INIT_FORM);
  const [formErr, setFormErr] = useState({});
  const [paymentFile, setPaymentFile] = useState(null);
  const [paymentTab, setPaymentTab] = useState('gpay');
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(() => loadPendingPayment());
  const [showPaymentReminder, setShowPaymentReminder] = useState(() => Boolean(loadPendingPayment()));
  const [toast, setToast] = useState(false);
  const [toastEmail, setToastEmail] = useState(OWNER_EMAIL);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalQty, setModalQty] = useState(1);
  const fileRef = useRef();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [catRes, prodRes, cartRes] = await Promise.all([
          api.getCategories(),
          api.getProducts(),
          api.getCart().catch(() => null),
        ]);
        if (cancelled) return;
        const catNames = (catRes.data || []).map((c) => c.category_name);
        setCategories(['All', ...catNames]);
        setProducts(
          (prodRes.data || []).map((p) => ({
            id: p.product_id,
            category: p.category_name,
            name: p.product_name,
            price: p.price,
            discPrice: p.discount_price,
            discountPercent: p.discount_percentage,
            image: p.product_image,
            youtube: p.youtube_link,
            videoId: youtubeId(p.youtube_link),
            unit: p.unit,
            sortorder: Number(p.sortorder ?? 0),
          }))
        );

        const saved = {};
        (cartRes?.data?.items || []).forEach((item) => {
          if (item.product_id && item.quantity > 0) {
            saved[item.product_id] = item.quantity;
          }
        });
        setCart(saved);

        setLoadError('');
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Failed to load products');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onSelectCategory = (e) => {
      const name = e.detail;
      if (name) setActiveCategory(name);
    };
    window.addEventListener('selectCategory', onSelectCategory);
    return () => window.removeEventListener('selectCategory', onSelectCategory);
  }, []);

  useEffect(() => {
    const onNavigateAway = () => {
      if (showCart && step === 'qr' && placedOrder?.orderId) {
        savePendingPayment(placedOrder);
        setShowPaymentReminder(true);
        setShowCart(false);
      }
    };
    window.addEventListener('navigateMainSite', onNavigateAway);
    return () => window.removeEventListener('navigateMainSite', onNavigateAway);
  }, [showCart, step, placedOrder]);

  const filtered = products
    .filter((p) => {
      const matchCategory = activeCategory === 'All' || p.category === activeCategory;
      const q = searchQuery.trim().toLowerCase();
      const matchSearch = !q || p.name.toLowerCase().includes(q);
      return matchCategory && matchSearch;
    })
    .sort((a, b) => (a.sortorder - b.sortorder) || (a.id - b.id));

  const totalCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const cartItems = products.filter((p) => cart[p.id] > 0);
  const totalPrice = cartItems.reduce((sum, p) => sum + p.discPrice * cart[p.id], 0);

  const syncAdd = async (id) => {
    setCart((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
    try { await api.addToCart(id, 1); } catch (_) { /* keep UI cart */ }
  };

  const decrease = async (id) => {
    const nextQty = (cart[id] || 0) - 1;
    setCart((prev) => {
      const qty = (prev[id] || 0) - 1;
      if (qty <= 0) { const next = { ...prev }; delete next[id]; return next; }
      return { ...prev, [id]: qty };
    });
    try {
      if (nextQty <= 0) await api.removeFromCart(id);
      else await api.updateCart(id, nextQty);
    } catch (_) { /* keep UI cart */ }
  };

  const remove = async (id) => {
    setCart((prev) => { const next = { ...prev }; delete next[id]; return next; });
    try { await api.removeFromCart(id); } catch (_) { /* keep UI cart */ }
  };

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setModalQty(Math.max(1, cart[product.id] || 1));
  };

  const closeProductModal = () => {
    setSelectedProduct(null);
    setModalQty(1);
  };

  const addModalToCart = async () => {
    if (!selectedProduct) return;
    const id = selectedProduct.id;
    const qty = Math.max(1, modalQty);
    setCart((prev) => ({ ...prev, [id]: (prev[id] || 0) + qty }));
    try {
      await api.addToCart(id, qty);
    } catch (_) { /* keep UI cart */ }
    closeProductModal();
  };

  const discount = (price, disc, pct) => {
    if (pct != null && !Number.isNaN(Number(pct))) return Math.round(Number(pct));
    if (!price) return 0;
    return Math.round(((price - disc) / price) * 100);
  };

  const handleClose = () => {
    if (step === 'qr' && placedOrder?.orderId) {
      savePendingPayment(placedOrder);
      setShowPaymentReminder(true);
      setShowCart(false);
      return;
    }

    setShowCart(false);
    setStep('cart');
    setFormErr({});
    setPaymentFile(null);
    setPaymentTab('gpay');
    setPaymentConfirmed(false);
    setPlacing(false);
    setUploadingProof(false);

    if (!placedOrder?.orderId) {
      setForm(INIT_FORM);
      setPlacedOrder(null);
      setShowPaymentReminder(false);
      clearPendingPayment();
    }
  };

  const handleBack = () => {
    if (step === 'qr') {
      setStep('form');
      return;
    }
    if (step === 'form') {
      setStep('cart');
      setFormErr({});
    }
  };

  const openCartPanel = () => {
    if (placedOrder?.orderId) {
      setStep('qr');
    } else {
      setStep('cart');
    }
    setShowPaymentReminder(false);
    setShowCart(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setFormErr((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const err = {};
    if (!form.name.trim()) err.name = 'Name is required';
    if (!/^[6-9]\d{9}$/.test(form.mobile)) err.mobile = 'Enter valid 10-digit mobile';
    if (!/\S+@\S+\.\S+/.test(form.email)) err.email = 'Enter valid email';
    if (!form.address.trim()) err.address = 'Address is required';
    if (!form.city.trim()) err.city = 'City is required';
    if (!form.state.trim()) err.state = 'State is required';
    if (!/^\d{6}$/.test(form.pincode)) err.pincode = 'Enter valid 6-digit pincode';
    if (!form.agree) err.agree = 'Please accept terms';
    return err;
  };

  const handleSubmitEnquiry = async () => {
    const err = validateForm();
    if (Object.keys(err).length) { setFormErr(err); return; }
    if (!cartItems.length) {
      alert('Your cart is empty.');
      return;
    }

    setPlacing(true);
    try {
      const items = cartItems.map((p) => ({ id: p.id, qty: cart[p.id] }));
      const orderTotal = totalPrice + PACKING_CHARGE;
      const res = await api.placeOrder(form, items, paymentTab);
      setToastEmail(form.email);
      const subtotal = res?.data?.subtotal ?? totalPrice;
      const total = res?.data?.total ?? orderTotal;
      setPlacedOrder({
        orderId: res?.data?.order_id,
        invoiceNo: res?.data?.invoice_no,
        subtotal,
        total,
        email: form.email,
      });
      savePendingPayment({
        orderId: res?.data?.order_id,
        invoiceNo: res?.data?.invoice_no,
        subtotal,
        total,
        email: form.email,
      });
      setShowPaymentReminder(false);
      setCart({});
      setStep('qr');
      if (res?.data?.email_preview) {
        console.log('Enquiry invoice email preview:', res.data.email_preview);
      }
    } catch (err) {
      alert(err.message || 'Failed to submit enquiry');
    } finally {
      setPlacing(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!placedOrder?.orderId) { alert('Order not found. Please go back and submit your details again.'); return; }

    setUploadingProof(true);
    try {
      const res = await api.uploadPaymentProof(placedOrder.orderId, paymentFile, paymentTab);
      if (res?.data?.email_preview) {
        console.log('Invoice email preview:', res.data.email_preview);
      }
      setShowCart(false);
      setStep('cart');
      setForm(INIT_FORM);
      setPaymentFile(null);
      setPaymentConfirmed(false);
      setPlacedOrder(null);
      setShowPaymentReminder(false);
      clearPendingPayment();
      setToast(true);
      setTimeout(() => setToast(false), 6000);
    } catch (err) {
      alert(err.message || 'Failed to place order');
    } finally {
      setUploadingProof(false);
    }
  };

  const productTotal = (() => {
    if (placedOrder?.subtotal != null) return Number(placedOrder.subtotal);
    if (totalPrice > 0) return totalPrice;
    const raw = Number(placedOrder?.total || 0);
    // New orders store total with shipping; older pending totals were product-only
    return raw > PACKING_CHARGE ? raw - PACKING_CHARGE : raw;
  })();
  const displayTotal = productTotal + PACKING_CHARGE;
  const showFloatCart = totalCount > 0 || Boolean(placedOrder?.orderId);
  const floatCartTotal = totalCount > 0 ? totalPrice : displayTotal;
  const floatCartBadge = totalCount > 0 ? totalCount : (placedOrder?.orderId ? '!' : 0);
  const offcanvasTitle = {
    cart: `My Cart (${totalCount})`,
    form: 'Delivery Details',
    qr: 'Scan & Pay',
  }[step] || 'Order';

  if (loading) {
    return (
      <section className="products-section" id="products">
        <Container fluid className="px-4 py-5 text-center">
          <Spinner animation="border" role="status" />
          <p className="mt-3 mb-0">Loading products…</p>
        </Container>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="products-section" id="products">
        <Container fluid className="px-4 py-5 text-center">
          <p className="mb-2">Could not load products from API.</p>
          <p className="text-muted">{loadError}</p>
          <p className="text-muted small">Make sure the backend is running on port 5001.</p>
        </Container>
      </section>
    );
  }

  return (
    <section className="products-section" id="products">
      <Container fluid className="px-4">

        <div className="category-bar">
          {categories.map((cat) => (
            <button key={cat} className={`category-pill ${activeCategory === cat ? 'category-pill--active' : ''}`} onClick={() => setActiveCategory(cat)}>
              {cat}
            </button>
          ))}
        </div>

        <div className="products-heading">
          <div className="products-heading__left">
            <h2>{activeCategory === 'All' ? 'All Products' : activeCategory}</h2>
            {/* <span className="products-count">{filtered.length} products</span> */}
          </div>
          <div className="products-search">
            <BsSearch className="products-search__icon" size={15} />
            <input
              type="search"
              className="products-search__input"
              placeholder="Search products…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search products"
            />
            {searchQuery && (
              <button
                type="button"
                className="products-search__clear"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <BsX size={18} />
              </button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="products-empty">No products found{searchQuery.trim() ? ` for “${searchQuery.trim()}”` : ''}.</p>
        ) : (
        <Row className="g-3">
          {filtered.map((product) => {
            const qty = cart[product.id] || 0;
            return (
              <Col key={product.id} xs={6} sm={4} md={4} lg={3} xl={2} className="col-xl-fifth">
                <div
                  className="product-card product-card--clickable"
                  onClick={() => openProductModal(product)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openProductModal(product);
                    }
                  }}
                >
                  <span className="product-card__badge">-{discount(product.price, product.discPrice, product.discountPercent)}%</span>
                  <div className="product-card__img-wrap">
                    <img
                      src={productImgSrc(product.image)}
                      alt={product.name}
                      loading="lazy"
                      onError={handleImgError}
                    />
                  </div>
                  <div className="product-card__body">
                    <p className="product-card__name">{product.name}</p>
                    <div className="product-card__prices">
                      <span className="product-card__disc-price">₹{product.discPrice}</span>
                      <span className="product-card__orig-price">₹{product.price}</span>
                    </div>
                  </div>
                  <div className="product-card__actions" onClick={(e) => e.stopPropagation()}>
                    {qty === 0 ? (
                      <button className="product-card__cart-btn" onClick={() => syncAdd(product.id)} aria-label="Add to cart">
                        <BsCart3 size={15} /><span>Add</span>
                      </button>
                    ) : (
                      <div className="product-card__qty">
                        <button className="product-card__qty-btn" onClick={() => decrease(product.id)} aria-label="Decrease"><BsDash size={14} /></button>
                        <span className="product-card__qty-num">{qty}</span>
                        <button className="product-card__qty-btn" onClick={() => syncAdd(product.id)} aria-label="Increase"><BsPlus size={14} /></button>
                      </div>
                    )}
                    {product.videoId && (
                      <a href={`https://www.youtube.com/watch?v=${product.videoId}`} target="_blank" rel="noopener noreferrer" className="product-card__yt-btn" aria-label="Watch video">
                        <BsYoutube size={18} />
                      </a>
                    )}
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
        )}
      </Container>

      <Modal
        show={!!selectedProduct}
        onHide={closeProductModal}
        centered
        size="lg"
        className="product-modal"
        contentClassName="product-modal__content"
      >
        {selectedProduct && (
          <>
            <button
              type="button"
              className="product-modal__close"
              onClick={closeProductModal}
              aria-label="Close"
            >
              <BsX size={28} />
            </button>
            <div className="product-modal__grid">
              <div className="product-modal__media">
                <span className="product-modal__badge">
                  -{discount(selectedProduct.price, selectedProduct.discPrice, selectedProduct.discountPercent)}%
                </span>
                <img
                  src={productImgSrc(selectedProduct.image)}
                  alt={selectedProduct.name}
                  onError={handleImgError}
                />
              </div>
              <div className="product-modal__details">
                <p className="product-modal__category">{selectedProduct.category}</p>
                <h3 className="product-modal__name">{selectedProduct.name}</h3>
                {selectedProduct.unit && (
                  <p className="product-modal__unit">Per {selectedProduct.unit}</p>
                )}
                <div className="product-modal__prices">
                  <span className="product-modal__disc-price">₹{selectedProduct.discPrice}</span>
                  <span className="product-modal__orig-price">₹{selectedProduct.price}</span>
                </div>

                {selectedProduct.videoId && (
                  <a
                    href={`https://www.youtube.com/watch?v=${selectedProduct.videoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="product-modal__yt-btn"
                  >
                    <BsYoutube size={20} />
                    <span>Watch Video</span>
                  </a>
                )}

                <div className="product-modal__qty-row">
                  <span className="product-modal__qty-label">Quantity</span>
                  <div className="product-modal__qty">
                    <button
                      type="button"
                      className="product-modal__qty-btn"
                      onClick={() => setModalQty((q) => Math.max(1, q - 1))}
                      aria-label="Decrease quantity"
                    >
                      <BsDash size={18} />
                    </button>
                    <span className="product-modal__qty-num">{modalQty}</span>
                    <button
                      type="button"
                      className="product-modal__qty-btn"
                      onClick={() => setModalQty((q) => q + 1)}
                      aria-label="Increase quantity"
                    >
                      <BsPlus size={18} />
                    </button>
                  </div>
                </div>

                <button type="button" className="product-modal__add-btn" onClick={addModalToCart}>
                  <BsCart3 size={18} />
                  <span>Add to Cart — ₹{selectedProduct.discPrice * modalQty}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>

      {showFloatCart && (
        <div className="float-cart-wrap">
          {showPaymentReminder && placedOrder?.orderId && !showCart && (
            <button
              type="button"
              className="float-cart-reminder"
              onClick={openCartPanel}
              aria-label="Complete your payment"
            >
              <span className="float-cart-reminder__text">Complete your payment</span>
              <span className="float-cart-reminder__arrow">→</span>
            </button>
          )}
          <button
            type="button"
            className={`float-cart-btn ${showPaymentReminder && placedOrder?.orderId ? 'float-cart-btn--pending' : ''}`}
            onClick={openCartPanel}
            aria-label={placedOrder?.orderId ? 'Complete payment' : 'Open cart'}
          >
            <span className="float-cart-btn__badge">{floatCartBadge}</span>
            <BsCart3 size={22} />
            <span className="float-cart-btn__price">₹{floatCartTotal}</span>
          </button>
        </div>
      )}

      <Offcanvas show={showCart} onHide={handleClose} placement="end" className="cart-offcanvas">
        <Offcanvas.Header closeButton closeVariant="white">
          <Offcanvas.Title className="cart-offcanvas__title">
            {(step === 'form' || step === 'qr') && (
              <button
                type="button"
                className="cart-offcanvas__back"
                onClick={handleBack}
                aria-label="Go back"
              >
                <BsArrowLeft size={20} />
              </button>
            )}
            <BsCart3 size={20} /> {offcanvasTitle}
          </Offcanvas.Title>
        </Offcanvas.Header>

        {step === 'cart' && (
          <>
            <Offcanvas.Body className="cart-offcanvas__body">
              {cartItems.length === 0 ? (
                <p className="cart-offcanvas__empty">Your cart is empty.</p>
              ) : (
                <div className="cart-offcanvas__list">
                  {cartItems.map((p) => (
                    <div key={p.id} className="cart-item">
                      <img
                        src={productImgSrc(p.image)}
                        alt={p.name}
                        className="cart-item__img"
                        onError={handleImgError}
                      />
                      <div className="cart-item__info">
                        <p className="cart-item__name">{p.name}</p>
                        <p className="cart-item__price">₹{p.discPrice} <span>× {cart[p.id]}</span></p>
                        <p className="cart-item__subtotal">₹{p.discPrice * cart[p.id]}</p>
                      </div>
                      <div className="cart-item__controls">
                        <button onClick={() => decrease(p.id)} aria-label="Decrease"><BsDash size={13} /></button>
                        <span>{cart[p.id]}</span>
                        <button onClick={() => syncAdd(p.id)} aria-label="Increase"><BsPlus size={13} /></button>
                        <button className="cart-item__remove" onClick={() => remove(p.id)} aria-label="Remove"><BsTrash size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Offcanvas.Body>
            {cartItems.length > 0 && (
              <div className="cart-offcanvas__footer">
                <div className="cart-offcanvas__total"><span>Total</span><span>₹{totalPrice}</span></div>
                <button className="cart-offcanvas__order-btn" onClick={() => setStep('form')}>Order Now</button>
              </div>
            )}
          </>
        )}

        {step === 'form' && (
          <>
            <Offcanvas.Body className="cart-offcanvas__body">
              <Form className="checkout-form">
                {[
                  { label: 'Full Name', name: 'name', type: 'text', placeholder: 'Enter your name' },
                  { label: 'Mobile Number', name: 'mobile', type: 'tel', placeholder: '10-digit mobile' },
                  { label: 'Email', name: 'email', type: 'email', placeholder: 'your@email.com' },
                  { label: 'Delivery Address', name: 'address', type: 'text', placeholder: 'Street / Area / Landmark' },
                  { label: 'City', name: 'city', type: 'text', placeholder: 'City' },
                  { label: 'State', name: 'state', type: 'text', placeholder: 'State' },
                  { label: 'Pincode', name: 'pincode', type: 'text', placeholder: '6-digit pincode' },
                ].map(({ label, name, type, placeholder }) => (
                  <Form.Group key={name} className="checkout-form__group">
                    <Form.Label className="checkout-form__label">{label}</Form.Label>
                    <Form.Control
                      type={type}
                      name={name}
                      value={form[name]}
                      onChange={handleFormChange}
                      placeholder={placeholder}
                      className={`checkout-form__input ${formErr[name] ? 'is-invalid' : ''}`}
                    />
                    {formErr[name] && <div className="checkout-form__error">{formErr[name]}</div>}
                  </Form.Group>
                ))}

                <Form.Group className="checkout-form__group checkout-form__check">
                  <Form.Check
                    type="checkbox"
                    name="agree"
                    id="agree"
                    checked={form.agree}
                    onChange={handleFormChange}
                    label="I agree to the terms & conditions and confirm my order details are correct."
                    className="checkout-form__checkbox"
                  />
                  {formErr.agree && <div className="checkout-form__error">{formErr.agree}</div>}
                </Form.Group>
              </Form>
            </Offcanvas.Body>
            <div className="cart-offcanvas__footer">
              <div className="cart-offcanvas__total"><span>Total</span><span>₹{totalPrice}</span></div>
              <button className="cart-offcanvas__order-btn" onClick={handleSubmitEnquiry} disabled={placing}>
                {placing ? 'Submitting…' : 'Order Now'}
              </button>
            </div>
          </>
        )}

        {step === 'qr' && (
          <>
            <Offcanvas.Body className="cart-offcanvas__body">
              <div className="payment-section">
                <p className="payment-section__label">Scan & Pay ₹{displayTotal}</p>

                <div className="payment-section__tabs">
                  <button
                    type="button"
                    className={`payment-section__tab ${paymentTab === 'gpay' ? 'payment-section__tab--active' : ''}`}
                    onClick={() => setPaymentTab('gpay')}
                  >
                    GPay
                  </button>
                  <button
                    type="button"
                    className={`payment-section__tab ${paymentTab === 'bank' ? 'payment-section__tab--active' : ''}`}
                    onClick={() => setPaymentTab('bank')}
                  >
                    Bank Transfer
                  </button>
                </div>

                {paymentTab === 'gpay' ? (
                  <div className="payment-section__qr">
                    <img src={qrimag} alt="Payment QR Code" />
                    <p className="payment-section__upi">UPI ID: aadvikyuigana@okicici</p>
                  </div>
                ) : (
                  <div className="payment-section__bank">
                    <p className="payment-section__bank-row">
                      <span className="payment-section__bank-label">Name :</span>
                      <span className="payment-section__bank-value">{BANK_DETAILS.name}</span>
                    </p>
                    <p className="payment-section__bank-row">
                      <span className="payment-section__bank-label">Branch :</span>
                      <span className="payment-section__bank-value">{BANK_DETAILS.branch}</span>
                    </p>
                    <p className="payment-section__bank-row">
                      <span className="payment-section__bank-label">Account Number :</span>
                      <span className="payment-section__bank-value">{BANK_DETAILS.accountNumber}</span>
                    </p>
                    <p className="payment-section__bank-row">
                      <span className="payment-section__bank-label">IFSC :</span>
                      <span className="payment-section__bank-value payment-section__bank-value--ifsc">{BANK_DETAILS.ifsc}</span>
                    </p>
                  </div>
                )}

                {/* {placedOrder?.invoiceNo && (
                  <p className="payment-section__invoice">Invoice: {placedOrder.invoiceNo}</p>
                )} */}

                <div className="payment-section__upload">
                  <p className="payment-section__upload-label">Upload Payment Proof <span className="payment-section__optional">(Optional)</span></p>
                  <button type="button" className="payment-section__upload-btn" onClick={() => fileRef.current.click()}>
                    <BsUpload size={16} />
                    {paymentFile ? paymentFile.name : 'Choose Screenshot / Receipt'}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => setPaymentFile(e.target.files[0])}
                  />
                  {paymentFile && <p className="payment-section__file-name">✅ {paymentFile.name}</p>}
                </div>

                <div className="payment-section__shipping">
                  <span>Packing Charges :</span>
                  <span>₹{PACKING_CHARGE}</span>
                </div>
              </div>
            </Offcanvas.Body>
            <div className="cart-offcanvas__footer">
              <Form.Check
                type="checkbox"
                id="payment-confirm"
                checked={paymentConfirmed}
                onChange={(e) => setPaymentConfirmed(e.target.checked)}
                label="Confirm you have shared the payment screenshot with us (optional)."
                className="payment-section__confirm"
              />
              <div className="cart-offcanvas__total"><span>Total</span><span>₹{productTotal}</span></div>
              <div className="cart-offcanvas__breakdown">
                Product + Packing Charges = <span>₹{displayTotal}</span>
              </div>
              <button
                className="cart-offcanvas__order-btn"
                onClick={handlePlaceOrder}
                disabled={uploadingProof}
              >
                {uploadingProof ? 'Placing…' : 'Place a order'}
              </button>
            </div>
          </>
        )}
      </Offcanvas>

      {toast && (
        <div className="order-toast" role="alert">
          <BsCheckCircleFill size={20} color="#4caf50" />
          <div>
            <p className="order-toast__title">Order Placed! 🎆</p>
            <p className="order-toast__sub">Invoice PDF sent to <strong>{toastEmail}</strong> and admin</p>
          </div>
        </div>
      )}
    </section>
  );
};
